// ─── src/api/meetupAttendees.ts ───────────────────────────────────────────────

import { apiClient } from "./registration";
import type {
  CheckInResponse,
  MeetupAttendee,
  MeetupAttendeeList,
  MeetupAttendeeParams,
} from "../types/meetupAttendee";
import type { PaginationMeta } from "../types/registration";

const BASE = "/meetup-attendees";

export interface NormalisedMeetupAttendees {
  data: MeetupAttendee[];
  meta: PaginationMeta;
}

const normalise = (raw: MeetupAttendeeList): NormalisedMeetupAttendees => ({
  data: raw.data,
  meta: {
    total:      raw.total,
    page:       raw.page,
    limit:      raw.limit,
    totalPages: Math.ceil(raw.total / raw.limit),
  },
});

export const getMeetupAttendees = async (
  params: MeetupAttendeeParams,
): Promise<NormalisedMeetupAttendees> => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null),
  );
  const { data } = await apiClient.get<MeetupAttendeeList>(BASE, { params: clean });
  return normalise(data);
};

/** Admin accept/reject decision — also fires the Mailchimp tag server-side. */
export const updateMeetupAttendeeApproval = async (
  id: string,
  accepted: boolean,
): Promise<void> => {
  await apiClient.patch(`${BASE}/${id}/approval`, { accepted });
};

/** Door check-in by scanned QR (accepts the bare token or the full ticket URL). */
export const checkInByToken = async (
  token: string,
  eventId?: string,
): Promise<CheckInResponse> => {
  const { data } = await apiClient.post<CheckInResponse>(`${BASE}/check-in`, {
    token,
    ...(eventId ? { eventId } : {}),
  });
  return data;
};

/** Manual check-in fallback when the camera or network fails. */
export const checkInById = async (id: string): Promise<CheckInResponse> => {
  const { data } = await apiClient.post<CheckInResponse>(`${BASE}/${id}/check-in`);
  return data;
};

/** Undo a mis-scan. */
export const undoCheckIn = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${id}/check-in`);
};

export const deleteMeetupAttendee = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${id}`);
};
